import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, apiError, formatINR, formatDate } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, X, Truck, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import BulkSelectBar from "../components/BulkSelectBar";
const BLANK_ITEM = {
  name: "", company: "", batch_number: "",
  manufacturing_date: "", expiry_date: "",
  mrp: "", purchase_price: "", quantity: "",
  location: "", barcode: "",
};

const newItem = () => ({
  ...BLANK_ITEM,
  _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
});

export default function Purchases() {
  const { user } = useAuth();
  const canBulkDelete = user?.role === "admin" || user?.role === "branch_head";
  const [list, setList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "", invoice_number: "", payment_mode: "credit", paid_amount: 0,
    date: new Date().toISOString().slice(0, 10), notes: "",
    gst_rate: 12, gst_inclusive: false,
    items: [{ ...BLANK_ITEM }],
  });

  const load = async () => {
    try {
      const [p, v] = await Promise.all([API.get("/purchases"), API.get("/vendors")]);
      setList(p.data); setVendors(v.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({
      vendor_id: vendors[0]?.id || "", invoice_number: "",
      payment_mode: "credit", paid_amount: 0,
      date: new Date().toISOString().slice(0, 10), notes: "",
      gst_rate: 12, gst_inclusive: false,
      items: [newItem()],
    });
    setModal(true);
  };

  const updItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, [key]: val } : x) }));
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, newItem()] }));
  const delRow = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, ix) => ix !== i) }));

  const subtotalCost = form.items.reduce((s, x) => s + (parseFloat(x.purchase_price) || 0) * (parseInt(x.quantity) || 0), 0);
  const gstRate = parseFloat(form.gst_rate) || 0;
  let taxable, gstAmount, total;
  if (form.gst_inclusive && gstRate > 0) {
    gstAmount = (subtotalCost * gstRate) / (100 + gstRate);
    taxable = subtotalCost - gstAmount;
    total = subtotalCost; // cost entered is already GST-inclusive
  } else {
    taxable = subtotalCost;
    gstAmount = (subtotalCost * gstRate) / 100;
    total = subtotalCost + gstAmount;
  }

  const save = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) { toast.error("Select a vendor"); return; }
    try {
      const payload = {
        ...form,
        gst_rate: gstRate,
        gst_inclusive: !!form.gst_inclusive,
        paid_amount: parseFloat(form.paid_amount) || 0,
        items: form.items.map((it) => ({
          ...it,
          mrp: parseFloat(it.mrp), purchase_price: parseFloat(it.purchase_price),
          quantity: parseInt(it.quantity),
        })),
      };
      await API.post("/purchases", payload);
      toast.success("Purchase recorded. Stock & vendor ledger updated.");
      setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delPurchase = async (p) => {
    if (!window.confirm(`Delete ${p.po_number} (${p.vendor_name})?\n\nStock will be reversed and the vendor ledger entries will be removed.`)) return;
    try {
      await API.delete(`/purchases/${p.id}`);
      toast.success(`Deleted ${p.po_number}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected purchases? Stock will be reversed and vendor ledger entries removed. This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/bulk-delete", { entity: "purchases", ids: [...selected] });
      toast.success(`${data.deleted} deleted${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="purchases-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Stock Inward</div>
          <h1 className="font-display text-3xl font-bold">Purchases</h1>
          <p className="text-sm text-slate-500 mt-1">Record purchase invoices. Stock is auto-incremented and vendor ledger is updated.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="new-purchase-btn" disabled={vendors.length === 0}>
          <Plus className="w-4 h-4" /> New Purchase
        </button>
      </div>

      {vendors.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Add a <Link to="/vendors" className="font-semibold underline">vendor</Link> first before recording purchases.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 font-display font-semibold">Recent Purchases</div>
        {canBulkDelete && (
          <BulkSelectBar
            count={selected.size}
            total={list.length}
            onClear={() => setSelected(new Set())}
            onSelectAll={() => setSelected(new Set(list.map((p) => p.id)))}
            onDelete={bulkDelete}
            busy={bulkBusy}
            entityLabel="purchases"
          />
        )}
        {list.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-500">No purchases recorded yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr>
                {canBulkDelete && (
                  <th className="w-8">
                    <input type="checkbox" data-testid="purchase-select-all"
                      checked={list.length > 0 && selected.size === list.length}
                      onChange={(e) => setSelected(e.target.checked ? new Set(list.map((p) => p.id)) : new Set())} />
                  </th>
                )}
                <th>Date</th><th>PO No</th><th>Vendor</th><th>Invoice</th><th>Mode</th>
                <th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Due</th>
              </tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} data-testid={`purchase-${p.id}`} className={selected.has(p.id) ? "bg-blue-50/50" : ""}>
                    {canBulkDelete && (
                      <td>
                        <input type="checkbox" data-testid={`purchase-select-${p.id}`}
                          checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} />
                      </td>
                    )}
                    <td>{formatDate(p.date)}</td>
                    <td className="font-mono text-xs">{p.po_number}</td>
                    <td><Link to={`/vendor/${p.vendor_id}`} className="font-medium hover:text-[#002FA7]">{p.vendor_name}</Link></td>
                    <td className="font-mono text-xs">{p.invoice_number || "—"}</td>
                    <td><span className={`tag ${p.payment_mode === "cash" ? "tag-success" : p.payment_mode === "credit" ? "tag-warning" : "tag-info"}`}>{p.payment_mode}</span></td>
                    <td className="text-right font-medium">{formatINR(p.total)}</td>
                    <td className="text-right text-emerald-700">{formatINR(p.paid)}</td>
                    <td className="text-right text-red-600 font-medium">{formatINR(p.balance_due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-display text-xl font-semibold">New Purchase</h3>
              <button type="button" onClick={() => setModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <L label="Vendor *">
                <select required value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="inp" data-testid="purchase-vendor-select">
                  <option value="">— Select —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </L>
              <L label="Invoice No"><input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="inp" /></L>
              <L label="Date"><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="inp" /></L>
              <L label="Payment Mode">
                <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="inp">
                  <option value="credit">Credit (on account)</option>
                  <option value="cash">Cash (paid in full)</option>
                  <option value="partial">Partial</option>
                </select>
              </L>
              {form.payment_mode === "partial" && (
                <L label="Paid Amount (₹)"><input type="number" step="0.01" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} className="inp" /></L>
              )}
              <L label="GST Rate (%)">
                <select value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} className="inp" data-testid="purchase-gst-rate">
                  <option value="0">0% (Exempt)</option>
                  <option value="5">5%</option>
                  <option value="12">12%</option>
                  <option value="18">18%</option>
                  <option value="28">28%</option>
                </select>
              </L>
              <L label="GST Handling">
                <label className="inline-flex items-center gap-2 text-sm h-[38px] px-3 border border-slate-200 rounded-lg bg-white cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={!!form.gst_inclusive}
                         onChange={(e) => setForm({ ...form, gst_inclusive: e.target.checked })}
                         data-testid="purchase-gst-inclusive" />
                  <span className="text-xs">Cost is <b>GST inclusive</b> (extract from price)</span>
                </label>
              </L>
              <L label="Notes" full={form.payment_mode !== "partial"}><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp" /></L>
            </div>

            <div className="px-5 pb-3 flex items-center justify-between">
              <div className="overline">Items</div>
              <button type="button" onClick={addRow} className="btn-ghost text-xs" data-testid="add-purchase-row"><Plus className="w-3.5 h-3.5" /> Add Row</button>
            </div>
            <div className="px-5 pb-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-2">Medicine</th><th className="py-2 pr-2">Company</th>
                    <th className="py-2 pr-2">Batch</th><th className="py-2 pr-2">Mfg</th><th className="py-2 pr-2">Exp</th>
                    <th className="py-2 pr-2">MRP</th><th className="py-2 pr-2">Cost</th>
                    <th className="py-2 pr-2">Qty</th><th className="py-2 pr-2">Loc</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => (
                    <tr key={it._key || i} className="border-t border-slate-100">
                      <td className="py-1 pr-1"><input required value={it.name} onChange={(e) => updItem(i, "name", e.target.value)} className="inp text-xs" /></td>
                      <td className="py-1 pr-1"><input required value={it.company} onChange={(e) => updItem(i, "company", e.target.value)} className="inp text-xs" /></td>
                      <td className="py-1 pr-1"><input required value={it.batch_number} onChange={(e) => updItem(i, "batch_number", e.target.value)} className="inp text-xs font-mono" /></td>
                      <td className="py-1 pr-1"><input required type="date" value={it.manufacturing_date} onChange={(e) => updItem(i, "manufacturing_date", e.target.value)} className="inp text-xs" /></td>
                      <td className="py-1 pr-1"><input required type="date" value={it.expiry_date} onChange={(e) => updItem(i, "expiry_date", e.target.value)} className="inp text-xs" /></td>
                      <td className="py-1 pr-1 w-20"><input required type="number" step="0.01" value={it.mrp} onChange={(e) => updItem(i, "mrp", e.target.value)} className="inp text-xs text-right" /></td>
                      <td className="py-1 pr-1 w-20"><input required type="number" step="0.01" value={it.purchase_price} onChange={(e) => updItem(i, "purchase_price", e.target.value)} className="inp text-xs text-right" /></td>
                      <td className="py-1 pr-1 w-16"><input required type="number" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} className="inp text-xs text-right" /></td>
                      <td className="py-1 pr-1 w-16"><input value={it.location} onChange={(e) => updItem(i, "location", e.target.value)} className="inp text-xs" /></td>
                      <td>
                        {form.items.length > 1 && (
                          <button type="button" onClick={() => delRow(i)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-5 border-t border-slate-200 flex items-center justify-between sticky bottom-0 bg-white">
              <div className="text-sm space-x-3">
                <span className="overline">{form.gst_inclusive ? "Gross (incl. GST)" : "Subtotal"}</span>
                <span className="font-mono">{formatINR(subtotalCost)}</span>
                {form.gst_inclusive && gstRate > 0 && (
                  <>
                    <span className="text-slate-400">·</span>
                    <span className="overline">Taxable (ex-GST)</span>
                    <span className="font-mono">{formatINR(taxable)}</span>
                  </>
                )}
                <span className="text-slate-400">·</span>
                <span className="overline">GST @ {gstRate}%</span>
                <span className="font-mono">{formatINR(gstAmount)}</span>
                <span className="text-slate-400">·</span>
                <span className="overline mr-2">Total</span>
                <span className="font-display text-2xl font-bold text-[#002FA7]" data-testid="purchase-grand-total">{formatINR(total)}</span>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="save-purchase-btn"><FileSpreadsheet className="w-4 h-4" /> Record Purchase</button>
              </div>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;font-size:.8rem;outline:none;font-family:inherit}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}
function L({ label, children, full }) { return <label className={`block ${full ? "md:col-span-4" : ""}`}><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
